import { useTranslation } from "@localization/useTranslation";
import { isNullOrWhitespace } from "@common/stringOperations";
import { ApprovalOption } from "../AnnualWheel.types";
import { useSelectableResponsiblesAssignees } from "../useSelectableResponsiblesAssignees";
import { ActivityFormProps } from "./ActivityForm.types";

export type ActivityPeopleSectionProps = Pick<ActivityFormProps, "model" | "onModelChange" | "showError" | "readOnly">;

export function useActivityPeopleSection(props: ActivityPeopleSectionProps) {
    const { translateString } = useTranslation();
    const model = props.model;
    const { selectableResponsibles, selectableAssignees, responsiblesLoading } = useSelectableResponsiblesAssignees(
        model.assignees,
        model.responsible,
        model.contractId
    );

    // Someone has to receive the task before it can be approved: the responsible, or a responsible per group company.
    const isSignOffEnabled = !isNullOrWhitespace(model.responsible) || (model.allGroupCompaniesHaveResponsible() && model.groupCompanies.length > 0);
    const approvalOptionRequiringResponsible = {
        disabled: !isSignOffEnabled,
        hoverText: isSignOffEnabled ? undefined : translateString("responsibleRequiredForApprovalTooltip"),
    };
    const approvalOptions = [
        { id: ApprovalOption.ApprovalNotRequired.toString(), name: translateString("approvalNotRequired") },
        {
            id: ApprovalOption.ApprovalNotRequiredResponsibleGetNotified.toString(),
            name: translateString("approvalNotRequiredResponsibleGetNotified"),
            ...approvalOptionRequiringResponsible,
        },
        {
            id: ApprovalOption.ApprovalRequiredByResponsible.toString(),
            name: translateString("approvalRequiredByResponsible"),
            ...approvalOptionRequiringResponsible,
        },
    ];

    // The task falls to the responsible while nobody is assigned, so the field shows that instead of looking empty.
    const assigneesPlaceholder =
        model.responsible && model.assignees.length === 0 ? selectableResponsibles?.find((user) => user.id === model.responsible)?.name : "";

    function onResponsibleChange(responsible: string | null) {
        const approval = model.allGroupCompaniesHaveResponsible() ? model.approval : ApprovalOption.ApprovalNotRequired;
        props.onModelChange({ responsible: responsible ?? undefined, approval });
    }

    function onAssigneesChange(assignees: Array<string>) {
        props.onModelChange({ assignees });
    }

    function onApprovalChange(approval: string | null) {
        props.onModelChange({ approval: Number(approval) });
    }

    return {
        selectableResponsibles,
        selectableAssignees,
        usersLoading: responsiblesLoading,
        approvalOptions,
        assigneesPlaceholder,
        showResponsibleError: props.showError && model.isResponsibleMissing(),
        onResponsibleChange,
        onAssigneesChange,
        onApprovalChange,
    };
}
